import { ENV_DEFAULTS, readPublicEnv } from '@/core/configs/env.defaults';
import { env } from '@/environments';

export function getIcmsTransparenteUrl(): string {
    return readPublicEnv('VITE_ICMS_TRANSPARENTE', env.icmsTransparente || ENV_DEFAULTS.icmsTransparente);
}

export function getPortalUrl(): string {
    const portalUrl = readPublicEnv('VITE_PORTAL_URL', env.portalUrl || '');
    return portalUrl || getIcmsTransparenteUrl();
}

export function getApiProxyUrl(): string {
    if (typeof window !== 'undefined') {
        const { origin, hostname, port } = window.location;
        if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
            return `${origin}/`;
        }
        if (port === '5173' || port === '3000') {
            return `${origin}/`;
        }
    }

    const proxy = readPublicEnv('VITE_API_PROXY', env.apiProxyUrl || ENV_DEFAULTS.apiProxy);
    return proxy.endsWith('/') ? proxy : `${proxy}/`;
}

export function buildAuthLoginHref(id: string): string {
    const normalized = normalizeLoginQuerystring(id);
    return `/auth/login?id=${encodeURIComponent(normalized)}`;
}

export function normalizeLoginQuerystring(id: string): string {
    const trimmed = id.trim();
    if (!trimmed) {
        return trimmed;
    }

    if (!/%[0-9A-Fa-f]{2}/.test(trimmed)) {
        return trimmed;
    }

    try {
        const decoded = decodeURIComponent(trimmed);
        return decoded.length > 0 ? decoded : trimmed;
    } catch {
        return trimmed;
    }
}

export function isAuthDebugEnabled(): boolean {
    return readPublicEnv('VITE_AUTH_DEBUG', '') === 'true';
}

export function redirectToPortal(reason?: string): void {
    if (reason) {
        console.error('[auth]', reason);
    }

    const url = getIcmsTransparenteUrl();
    const delayMs = isAuthDebugEnabled() ? 8000 : 0;

    if (delayMs > 0) {
        console.warn(`[auth] Redirecionando ao portal em ${delayMs / 1000}s…`, url);
        window.setTimeout(() => window.location.replace(url), delayMs);
        return;
    }

    window.location.replace(url);
}

export function getLoginIdFromUrl(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const fromSearch = new URLSearchParams(window.location.search).get('id');
    if (fromSearch?.trim()) {
        return fromSearch.trim();
    }

    const hash = window.location.hash.replace(/^#/, '').trim();
    if (!hash) {
        return null;
    }

    if (hash.startsWith('id=')) {
        return hash.slice(3).trim() || null;
    }

    if (!hash.includes('=')) {
        return hash;
    }

    return new URLSearchParams(hash).get('id');
}

/** Marca que o usuário escolheu login nesta sessão do browser (só local). */
export const AUTH_LOCAL_COMPLETED_KEY = 'authLocalCompleted';

export function isAuthLocalCompleted(): boolean {
    try {
        return sessionStorage.getItem(AUTH_LOCAL_COMPLETED_KEY) === '1';
    } catch {
        return false;
    }
}

export function markAuthLocalCompleted(): void {
    try {
        sessionStorage.setItem(AUTH_LOCAL_COMPLETED_KEY, '1');
    } catch {
        /* ignore */
    }
}

export function clearAuthLocalCompleted(): void {
    try {
        sessionStorage.removeItem(AUTH_LOCAL_COMPLETED_KEY);
    } catch {
        /* ignore */
    }
}

export function shouldSkipAuthLogin(): boolean {
    return readPublicEnv('VITE_SKIP_AUTH_LOGIN', '') === 'true';
}
